// src/components/admin/CostAnalyticsTab.jsx
// 费用分析中心：按 日/月/季 维度查看项目与个人消费。
// 数据全部来自 /api/admin/costs/* 现有接口（权威账本 ai_generation_task），前端只做聚合与展示。
import { appConfirm } from '../../services/appDialogs';
import { AppSelect } from '../ui/AppSelect';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarRange, RefreshCw, X } from "lucide-react";
import {
  fetchCosts,
  fetchCostTrend,
  fetchCostsByUser,
  fetchCostsByProject,
  fetchCostsByModel,
  backfillMissingCosts,
  recomputeTaskCosts,
} from "../../services/adminService";
import {
  displayUserId,
  formatCostSpec,
  shortText,
  formatMoney,
  formatDetailedMoney,
} from "./adminUtils";
import { Panel, Segmented, EmptyBlock, TypeBadge, ProjectCombobox } from "./adminUi.jsx";
import {
  resolveCostTimeRange,
  toLocalDateKey,
  toLocalMonthKey,
} from "./costAnalyticsRange.js";

const ACCENT = "var(--color-warning)";

const TIME_MODES = [
  { key: "month", label: "指定月份" },
  { key: "custom", label: "自定义" },
  { key: "quick", label: "快捷范围" },
];

const RANGES = [
  { key: "7d", label: "近 7 天" },
  { key: "30d", label: "近 30 天" },
  { key: "90d", label: "近 90 天" },
  { key: "12m", label: "近 12 个月" },
  { key: "all", label: "全部" },
];

const GRANULARITIES = [
  { key: "day", label: "日" },
  { key: "month", label: "月" },
  { key: "quarter", label: "季" },
];

const TYPES = [
  { key: "", label: "全部" },
  { key: "image", label: "图片" },
  { key: "video", label: "视频" },
  { key: "text", label: "文本" },
  { key: "audio", label: "音频" },
];

const TYPE_META = {
  image: { label: "图片", color: "var(--color-blue-primary)" },
  video: { label: "视频", color: "var(--color-purple-primary)" },
  text: { label: "文本", color: "var(--color-green-primary)" },
  audio: { label: "音频", color: "var(--color-teal-primary)" },
};

const pad2 = (n) => String(n).padStart(2, "0");

const dateKeyOf = (d) => toLocalDateKey(d);
const monthKeyOf = (d) => toLocalMonthKey(d);

const emptyBucket = (key) => ({ key, total: 0, count: 0, image: 0, video: 0, text: 0, audio: 0 });

// 服务器只返回有数据的桶（稀疏），这里按所选范围补齐零值桶，让时间轴连续。
// 后端 bucket：daily=YYYY-MM-DD，monthly=YYYY-MM。
const buildTrendRows = (rows, granularity, timeRange) => {
  const list = (rows || []).map((row) => ({
    key: String(row.bucket),
    total: Number(row.totalCost || 0),
    count: Number(row.count || 0),
    image: Number(row.imageCount || 0),
    video: Number(row.videoCount || 0),
    text: Number(row.textCount || 0),
    audio: Number(row.audioCount || 0),
  }));
  const byKey = new Map(list.map((r) => [r.key, r]));
  const endAt = new Date(timeRange?.endTime ?? Date.now());

  // 起点：指定月份/自定义/快捷范围使用明确起点；“全部”使用最早一条数据。
  const resolveStart = (fallbackDays) => {
    if (timeRange?.startTime != null) return new Date(timeRange.startTime);
    if (!list.length) return null;
    const earliest = list.map((r) => r.key).sort()[0];
    const parsed = new Date(earliest.length === 7 ? `${earliest}-01` : earliest);
    return Number.isNaN(parsed.getTime()) ? new Date(endAt.getTime() - fallbackDays * 86400000) : parsed;
  };

  if (granularity === "day") {
    const start = resolveStart(30);
    if (!start) return [];
    const out = [];
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const end = new Date(endAt.getFullYear(), endAt.getMonth(), endAt.getDate());
    for (let i = 0; i < 1200 && cur <= end; i += 1, cur.setDate(cur.getDate() + 1)) {
      const key = dateKeyOf(cur);
      out.push({ ...(byKey.get(key) || emptyBucket(key)), label: key.slice(5) });
    }
    return out;
  }

  if (granularity === "month") {
    const start = resolveStart(365);
    if (!start) return [];
    const out = [];
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const end = new Date(endAt.getFullYear(), endAt.getMonth(), 1);
    for (let i = 0; i < 240 && cur <= end; i += 1, cur.setMonth(cur.getMonth() + 1)) {
      const key = monthKeyOf(cur);
      out.push({ ...(byKey.get(key) || emptyBucket(key)), label: key });
    }
    return out;
  }

  // quarter：先把月度数据聚合到季，再按范围补齐空季
  const quarters = new Map();
  for (const row of list) {
    const [y, m] = row.key.split("-").map(Number);
    if (!y || !m) continue;
    const q = Math.floor((m - 1) / 3) + 1;
    const key = `${y}-Q${q}`;
    if (!quarters.has(key)) quarters.set(key, emptyBucket(key));
    const bucket = quarters.get(key);
    bucket.total += row.total;
    bucket.count += row.count;
    bucket.image += row.image;
    bucket.video += row.video;
    bucket.text += row.text;
    bucket.audio += row.audio;
  }
  const start = resolveStart(365);
  if (!start) return [];
  const out = [];
  let y = start.getFullYear();
  let q = Math.floor(start.getMonth() / 3) + 1;
  const endY = endAt.getFullYear();
  const endQ = Math.floor(endAt.getMonth() / 3) + 1;
  for (let i = 0; i < 80 && (y < endY || (y === endY && q <= endQ)); i += 1) {
    const key = `${y}-Q${q}`;
    const bucket = quarters.get(key) || emptyBucket(key);
    out.push({ ...bucket, total: Math.round(bucket.total * 100) / 100, label: `${y} Q${q}` });
    q += 1;
    if (q > 4) { q = 1; y += 1; }
  }
  return out;
};

// ============================
// 趋势图（纯 HTML/CSS 柱状图 + 网格线 + 悬停详情）
// ============================

const TrendChart = ({ rows, granularity }) => {
  const [hover, setHover] = useState(null);
  const max = Math.max(...rows.map((r) => r.total), 0.01);
  const step = Math.max(1, Math.ceil(rows.length / 14));
  const gridFractions = [0.25, 0.5, 0.75, 1];

  if (!rows.length) {
    return <EmptyBlock text="该时间范围内没有费用记录" hint="调整时间范围或筛选条件" />;
  }

  return (
    <div>
      <div className="relative" style={{ height: 216 }}>
        {/* 网格线与 Y 轴刻度 */}
        {gridFractions.map((f) => (
          <div key={f} className="absolute left-0 right-0" style={{ bottom: `${f * 100}%` }}>
            <span
              className="absolute -top-2 left-0 font-mono-num"
              style={{ fontSize: 10, color: "var(--color-text-muted)" }}
            >
              {formatMoney(max * f)}
            </span>
            <div className="w-full" style={{ borderTop: "1px solid var(--color-border-secondary)", marginLeft: 64 }} />
          </div>
        ))}
        {/* 柱 */}
        <div className="absolute inset-0 flex items-end gap-[3px]" style={{ left: 72 }}>
          {rows.map((row, i) => {
            const pct = Math.max(row.total > 0 ? 1.5 : 0, (row.total / max) * 100);
            const active = hover === i;
            return (
              <div
                key={row.key}
                className="flex-1 h-full flex flex-col justify-end relative"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <div
                  className="w-full transition-colors duration-150"
                  style={{
                    height: `${pct}%`,
                    minHeight: row.total > 0 ? 2 : 0,
                    backgroundColor: ACCENT,
                    opacity: active ? 1 : 0.72,
                    borderRadius: "3px 3px 0 0",
                  }}
                />
                {active && (
                  <div
                    className="absolute bottom-full mb-2 z-20 rounded-lg border px-3 py-2.5 whitespace-nowrap"
                    style={{
                      left: i > rows.length * 0.75 ? "auto" : "50%",
                      right: i > rows.length * 0.75 ? 0 : "auto",
                      transform: i > rows.length * 0.75 ? "none" : "translateX(-50%)",
                      backgroundColor: "var(--color-bg-overlay)",
                      borderColor: "var(--color-border-primary)",
                      boxShadow: "var(--shadow-dropdown)",
                    }}
                  >
                    <div className="font-mono-num text-2xs mb-1.5" style={{ color: "var(--color-text-muted)" }}>{row.label}</div>
                    <div className="font-mono-num text-sm font-semibold" style={{ color: ACCENT }}>{formatMoney(row.total)}</div>
                    <div className="mt-1.5 space-y-0.5 text-2xs font-mono-num" style={{ color: "var(--color-text-secondary)" }}>
                      <div>任务 {row.count}</div>
                      <div>图 {row.image} · 视 {row.video} · 文 {row.text} · 音 {row.audio}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* X 轴标签 */}
      <div className="flex mt-1.5" style={{ paddingLeft: 72 }}>
        {rows.map((row, i) => (
          <div key={row.key} className="flex-1 text-center overflow-hidden">
            {i % step === 0 && (
              <span className="font-mono-num" style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
                {granularity === "day" ? row.label : row.label}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================
// 费用构成环形图（SVG donut + 图例）
// ============================

const DonutChart = ({ segments, total }) => {
  const R = 52;
  const C = 2 * Math.PI * R;
  if (!segments.length || total <= 0) {
    return <EmptyBlock text="该范围内没有构成数据" hint="调整时间范围或筛选条件" />;
  }
  // 先算好各弧段的起始偏移，渲染期不做任何累加赋值
  const arcs = [];
  {
    let acc = 0;
    for (const seg of segments) {
      const len = (seg.value / total) * C;
      arcs.push({ seg, len, start: acc });
      acc += len;
    }
  }
  return (
    <div className="flex items-center gap-5 flex-wrap">
      <svg width="150" height="150" viewBox="0 0 150 150" className="shrink-0">
        <circle cx="75" cy="75" r={R} fill="none" strokeWidth="16" style={{ stroke: "var(--color-bg-tertiary)" }} />
        {arcs.map(({ seg, len, start }) => (
          <circle
            key={seg.key}
            cx="75" cy="75" r={R} fill="none" strokeWidth="16"
            strokeDasharray={`${Math.max(len - 1.5, 0.5)} ${C - len + 1.5}`}
            strokeDashoffset={-start}
            transform="rotate(-90 75 75)"
            style={{ stroke: seg.color }}
          />
        ))}
        <text x="75" y="72" textAnchor="middle" fontSize="14" fontWeight="600" fill="var(--color-text-primary)">{formatMoney(total)}</text>
        <text x="75" y="90" textAnchor="middle" fontSize="10" fill="var(--color-text-muted)">总费用</text>
      </svg>
      <div className="space-y-1.5 min-w-[150px] flex-1">
        {segments.map((seg) => (
          <div key={seg.key} className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="flex-1" style={{ color: "var(--color-text-secondary)" }}>{seg.label}</span>
            <span className="font-mono-num" style={{ color: "var(--color-text-primary)" }}>{formatMoney(seg.value)}</span>
            <span className="font-mono-num w-11 text-right" style={{ color: "var(--color-text-muted)" }}>{((seg.value / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================
// 排行面板（点击行 = 交叉筛选）
// ============================

const RankPanel = ({ title, sub, rows, keyOf, nameOf, subOf, activeKey, onSelect, emptyHint }) => {
  const max = Math.max(...rows.map((r) => Number(r.totalCost || 0)), 0.01);
  return (
    <Panel
      title={title}
      sub={sub}
      right={<span className="text-label">点击行可筛选</span>}
    >
      {!rows.length ? (
        <EmptyBlock text="暂无数据" hint={emptyHint} />
      ) : (
        <div className="space-y-1 max-h-[340px] overflow-y-auto custom-scrollbar -mr-1 pr-1">
          {rows.map((row, idx) => {
            const key = keyOf(row);
            const active = activeKey === key;
            const share = Number(row.totalCost || 0) / max;
            return (
              <button
                key={key ?? idx}
                type="button"
                onClick={() => onSelect(active ? null : key, row)}
                className={`w-full text-left rounded-md px-2.5 py-2 transition-colors duration-150 cursor-pointer ${active ? '' : 'hover:bg-[var(--color-bg-tertiary)]'}`}
                style={active ? {
                  backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)",
                  boxShadow: "inset 2px 0 0 var(--color-warning)",
                } : undefined}
              >
                <div className="flex items-center gap-2.5">
                  <span className="font-mono-num text-2xs w-6 shrink-0" style={{ color: "var(--color-text-muted)" }}>
                    {pad2(idx + 1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium truncate" style={{ color: "var(--color-text-primary)" }}>{nameOf(row)}</div>
                    <div className="text-2xs truncate mt-0.5" style={{ color: "var(--color-text-muted)" }}>{subOf(row)}</div>
                  </div>
                  <span className="font-mono-num text-[13px] font-semibold shrink-0" style={{ color: ACCENT }}>
                    {formatMoney(row.totalCost)}
                  </span>
                </div>
                <div className="mt-1.5 ml-[34px] h-1 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.max(2, share * 100)}%`, backgroundColor: ACCENT, opacity: 0.75 }} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Panel>
  );
};

// ============================
// 主组件
// ============================

const CostAnalyticsTab = ({ projectId, projectOptions }) => {
  const [granularity, setGranularity] = useState("day");
  const [timeMode, setTimeMode] = useState("month");
  const [quickRange, setQuickRange] = useState("30d");
  const [selectedMonth, setSelectedMonth] = useState(() => toLocalMonthKey(Date.now()));
  const [customStartDate, setCustomStartDate] = useState(() => `${toLocalMonthKey(Date.now())}-01`);
  const [customEndDate, setCustomEndDate] = useState(() => toLocalDateKey(Date.now()));
  const [type, setType] = useState("");
  const [scopeProjectId, setScopeProjectId] = useState(projectId || "");
  const [scopeUserId, setScopeUserId] = useState("");
  const [userInput, setUserInput] = useState("");
  const [limit, setLimit] = useState(300);

  const [trend, setTrend] = useState(null);
  const [byProject, setByProject] = useState([]);
  const [byUser, setByUser] = useState([]);
  const [byModel, setByModel] = useState([]);
  const [detail, setDetail] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const [maintenanceResult, setMaintenanceResult] = useState(null);
  const [engGuaVideoRecomputeResult, setEngGuaVideoRecomputeResult] = useState(null);
  const [engGuaVideoRecomputing, setEngGuaVideoRecomputing] = useState(false);
  const [providerFilter, setProviderFilter] = useState("");
  const [modelNameFilter, setModelNameFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const requestSeq = useRef(0);

  const timeRange = useMemo(() => resolveCostTimeRange({
    mode: timeMode,
    quickRange,
    month: selectedMonth,
    startDate: customStartDate,
    endDate: customEndDate,
  }), [timeMode, quickRange, selectedMonth, customStartDate, customEndDate]);

  useEffect(() => {
    setScopeProjectId(projectId || "");
  }, [projectId]);

  // 用户 ID 输入防抖，避免每个字符都触发请求
  useEffect(() => {
    const timer = setTimeout(() => setScopeUserId(userInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [userInput]);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    if (timeRange.error) {
      setLoading(false);
      setError(timeRange.error);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const base = {
        projectId: scopeProjectId || undefined,
        userId: scopeUserId || undefined,
        type: type || undefined,
        startTime: timeRange.startTime ?? undefined,
        endTime: timeRange.endTime,
      };
      const [trendRes, projectRes, userRes, modelRes, detailRes] = await Promise.all([
        fetchCostTrend({ ...base, bucket: granularity === "day" ? "daily" : "monthly" }),
        fetchCostsByProject({ ...base, limit: 15 }),
        fetchCostsByUser({ ...base, limit: 15 }),
        fetchCostsByModel({ ...base, limit: 500 }),
        fetchCosts({ ...base, limit }),
      ]);
      if (seq !== requestSeq.current) return; // 已有更新的请求，丢弃旧响应
      setTrend(trendRes || null);
      setByProject(projectRes?.rows || []);
      setByUser(userRes?.rows || []);
      setByModel(modelRes?.rows || []);
      setDetail(detailRes || null);
      setLoadedOnce(true);
    } catch (e) {
      console.error(e);
      if (seq === requestSeq.current) setError(e?.message || "费用数据加载失败");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [granularity, timeRange, type, scopeProjectId, scopeUserId, limit]);

  useEffect(() => { load(); }, [load]);

  const trendRows = useMemo(
    () => buildTrendRows(trend?.rows, granularity, timeRange),
    [trend, granularity, timeRange],
  );

  const kpis = useMemo(() => {
    const rows = trendRows;
    const sum = (key) => rows.reduce((acc, row) => acc + (row[key] || 0), 0);
    return {
      totalCost: trend?.totalCost ?? rows.reduce((acc, row) => acc + row.total, 0),
      count: sum("count"),
      image: sum("image"),
      video: sum("video"),
      text: sum("text"),
      audio: sum("audio"),
    };
  }, [trendRows, trend]);

  const projectNameMap = useMemo(() => {
    const map = new Map();
    (projectOptions || []).forEach((p) => map.set(String(p.id), p.name || `项目 ${p.id}`));
    byProject.forEach((row) => {
      if (row.projectId != null && row.projectName) map.set(String(row.projectId), row.projectName);
    });
    return map;
  }, [projectOptions, byProject]);

  const projectLabel = (id) => {
    if (id == null || id === "") return "未归属项目";
    return projectNameMap.get(String(id)) || `项目 ${id}`;
  };

  // 费用构成（按类型聚合，来自 by-model 接口，覆盖前 50 条线路，构成完整）
  const typeSegments = useMemo(() => {
    const acc = new Map();
    (byModel || []).forEach((row) => {
      const t = row.type || "other";
      acc.set(t, (acc.get(t) || 0) + Number(row.totalCost || 0));
    });
    return [...acc.entries()]
      .map(([key, value]) => ({
        key,
        label: TYPE_META[key]?.label || key,
        color: TYPE_META[key]?.color || "var(--color-text-muted)",
        value,
      }))
      .filter((s) => s.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [byModel]);

  const typeTotal = useMemo(() => typeSegments.reduce((a, s) => a + s.value, 0), [typeSegments]);

  // 线路筛选（客户端，只影响构成表展示）
  const providerOptions = useMemo(() => {
    const set = new Set();
    (byModel || []).forEach((row) => { if (row.provider) set.add(row.provider); });
    return [...set].sort();
  }, [byModel]);

  const modelNameOptions = useMemo(() => {
    const set = new Set();
    (byModel || []).forEach((row) => {
      if (row.model) set.add(row.model);
      if (row.subModel) set.add(row.subModel);
    });
    return [...set].sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [byModel]);

  const filteredByModel = useMemo(() => {
    return (byModel || []).filter((row) => {
      if (providerFilter && row.provider !== providerFilter) return false;
      if (modelNameFilter && row.model !== modelNameFilter && row.subModel !== modelNameFilter) return false;
      return true;
    });
  }, [byModel, providerFilter, modelNameFilter]);

  const selectedModelSummary = useMemo(() => filteredByModel.reduce((summary, row) => ({
    count: summary.count + Number(row.count || 0),
    totalCost: summary.totalCost + Number(row.totalCost || 0),
    promptTokens: summary.promptTokens + Number(row.promptTokens || 0),
    completionTokens: summary.completionTokens + Number(row.completionTokens || 0),
    videoSeconds: summary.videoSeconds + Number(row.videoSeconds || 0),
  }), { count: 0, totalCost: 0, promptTokens: 0, completionTokens: 0, videoSeconds: 0 }), [filteredByModel]);

  // 来源筛选（客户端，只影响明细表展示）
  const filteredDetailRows = useMemo(() => {
    const rows = detail?.rows || [];
    const q = sourceFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => String(row.source || "").toLowerCase().includes(q));
  }, [detail, sourceFilter]);

  const runBackfill = async (dryRun) => {
    if (!dryRun && !(await appConfirm("确认回填最多 1000 条 cost 为空的历史费用记录？"))) return;
    setError("");
    setMaintenanceResult(null);
    try {
      const result = await backfillMissingCosts(1000, dryRun);
      setMaintenanceResult(result);
      if (!dryRun) await load();
    } catch (e) {
      console.error(e);
      setError(e?.message || "费用回填失败");
    }
  };

  const runEngGuaVideoRecompute = async () => {
    if (engGuaVideoRecomputing) return;
    if (!(await appConfirm("确认按当前 GuaVideo 价表重算 EngGuaVideo 历史零费用记录？已确认的非零费用不会修改。"))) return;
    setError("");
    setEngGuaVideoRecomputeResult(null);
    setEngGuaVideoRecomputing(true);
    try {
      const result = await recomputeTaskCosts({
        provider: "engguavideo",
        onlyZeroOrNull: true,
      });
      setEngGuaVideoRecomputeResult(result);
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.message || "EngGuaVideo 历史费用重算失败");
    } finally {
      setEngGuaVideoRecomputing(false);
    }
  };

  const hasScope = scopeProjectId || scopeUserId;

  const kpiItems = [
    ["总费用", formatMoney(kpis.totalCost), ACCENT],
    ["任务数", kpis.count, "var(--color-text-primary)"],
    ["图片", kpis.image, TYPE_META.image.color],
    ["视频", kpis.video, TYPE_META.video.color],
    ["文本", kpis.text, TYPE_META.text.color],
    ["音频", kpis.audio, TYPE_META.audio.color],
  ];

  return (
    <div className="space-y-4">
      {/* 控制条 */}
      <div
        className="rounded-lg border px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-3"
        style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-secondary)" }}
      >
        <div className="w-full flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <CalendarRange size={15} style={{ color: ACCENT }} aria-hidden="true" />
            <span className="text-label">统计时间</span>
            <Segmented options={TIME_MODES} value={timeMode} onChange={setTimeMode} ariaLabel="统计时间选择方式" />
          </div>
          {timeMode === "month" && (
            <label className="flex items-center gap-2">
              <span className="sr-only">选择月份</span>
              <input
                type="month"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                className="h-9 min-w-[148px] rounded-md px-3 text-xs outline-none"
                style={{ colorScheme: "dark", backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
              />
            </label>
          )}
          {timeMode === "custom" && (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="自定义统计日期">
              <label>
                <span className="sr-only">开始日期</span>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(event) => setCustomStartDate(event.target.value)}
                  className="h-9 rounded-md px-3 text-xs outline-none"
                  style={{ colorScheme: "dark", backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
                />
              </label>
              <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>至</span>
              <label>
                <span className="sr-only">结束日期</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(event) => setCustomEndDate(event.target.value)}
                  className="h-9 rounded-md px-3 text-xs outline-none"
                  style={{ colorScheme: "dark", backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
                />
              </label>
            </div>
          )}
          {timeMode === "quick" && (
            <Segmented options={RANGES} value={quickRange} onChange={setQuickRange} ariaLabel="快捷时间范围" />
          )}
          <span
            className="ml-auto rounded-md border px-2.5 py-1.5 text-2xs font-mono-num"
            style={{ borderColor: "var(--color-border-secondary)", color: timeRange.error ? "var(--color-error)" : "var(--color-text-secondary)", backgroundColor: "var(--color-bg-primary)" }}
          >
            {timeRange.error || timeRange.label}
          </span>
        </div>
        <div
          className="w-full flex flex-wrap items-center gap-x-4 gap-y-3 border-t pt-3"
          style={{ borderColor: "var(--color-border-secondary)" }}
        >
          <div className="flex items-center gap-2">
            <span className="text-label">粒度</span>
            <Segmented options={GRANULARITIES} value={granularity} onChange={setGranularity} ariaLabel="时间粒度" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-label">类型</span>
            <Segmented options={TYPES} value={type} onChange={setType} ariaLabel="费用类型" />
          </div>
          <div className="flex flex-wrap items-center gap-2 ml-auto">
          <ProjectCombobox
            value={scopeProjectId}
            onChange={setScopeProjectId}
            projectOptions={projectOptions || []}
            maxWidth={220}
          />
          <input
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="用户 ID"
            aria-label="按用户 ID 筛选费用"
            className="h-8 px-2.5 rounded-md text-xs w-32 outline-none"
            style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
          />
          <button
            type="button"
            onClick={load}
            className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-secondary)" }}
            title="刷新费用数据"
            aria-label="刷新费用数据"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
          </div>
        </div>
        {/* 激活的交叉筛选 */}
        {hasScope && (
          <div className="w-full flex flex-wrap items-center gap-2 pt-1">
            <span className="text-label">当前筛选</span>
            {scopeProjectId && (
              <button
                type="button"
                onClick={() => setScopeProjectId("")}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-2xs font-medium border cursor-pointer transition-colors duration-150"
                style={{ color: ACCENT, borderColor: "color-mix(in srgb, var(--color-warning) 40%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)" }}
                title="点击清除"
              >
                项目：{projectLabel(scopeProjectId)} <X size={11} />
              </button>
            )}
            {scopeUserId && (
              <button
                type="button"
                onClick={() => { setScopeUserId(""); setUserInput(""); }}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-2xs font-medium border cursor-pointer transition-colors duration-150"
                style={{ color: ACCENT, borderColor: "color-mix(in srgb, var(--color-warning) 40%, transparent)", backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)" }}
                title="点击清除"
              >
                用户：{displayUserId(scopeUserId)} <X size={11} />
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg px-3.5 py-2.5 text-sm animate-in fade-in slide-in-from-top-1 duration-200" style={{ backgroundColor: "var(--color-red-light)", border: "1px solid var(--color-error)", color: "var(--color-error)" }}>
          {error}
        </div>
      )}

      {/* KPI 数据带 */}
      <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-secondary)" }}>
        <div className="grid grid-cols-3 md:grid-cols-6">
          {kpiItems.map(([label, value, color], index) => (
            <div key={label} className="px-4 py-3" style={index > 0 ? { borderLeft: "1px solid var(--color-border-secondary)" } : undefined}>
              <div className="text-label">{label}</div>
              <div className="font-mono-num text-xl leading-7 font-semibold mt-1 truncate" style={{ color }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 趋势 */}
      <Panel
        title="费用趋势"
        sub={`${timeRange.label} · 按${granularity === "day" ? "日" : granularity === "month" ? "月" : "季"}统计 · 悬停查看明细`}
        right={<span className="font-mono-num text-2xs" style={{ color: "var(--color-text-muted)" }}>{trendRows.length} 个统计点</span>}
      >
        {!loadedOnce && loading ? (
          <div className="space-y-2">
            <div className="skeleton" style={{ height: 216 }} />
            <div className="skeleton h-3 w-1/3" />
          </div>
        ) : (
          <TrendChart rows={trendRows} granularity={granularity} />
        )}
      </Panel>

      {/* 项目 / 用户 双排行 */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <RankPanel
          title="项目消费排行"
          sub="按项目聚合"
          rows={byProject}
          keyOf={(row) => (row.projectId == null ? "__none__" : String(row.projectId))}
          nameOf={(row) => projectLabel(row.projectId)}
          subOf={(row) => `${displayUserId(row.userId)} · ${row.count || 0} 次 · 视频 ${row.videoCount || 0} 条`}
          activeKey={scopeProjectId === "" ? null : (scopeProjectId || null)}
          onSelect={(key) => setScopeProjectId(key === "__none__" ? "" : (key || ""))}
          emptyHint="该范围内没有项目消费"
        />
        <RankPanel
          title="用户消费排行"
          sub="按用户聚合"
          rows={byUser}
          keyOf={(row) => row.userId || "__none__"}
          nameOf={(row) => displayUserId(row.userId, "未归属")}
          subOf={(row) => `${row.count || 0} 次 · 图 ${row.imageCount || 0} · 视 ${row.videoCount || 0} · 文 ${row.textCount || 0}`}
          activeKey={scopeUserId || null}
          onSelect={(key) => { const v = key === "__none__" ? "" : (key || ""); setScopeUserId(v); setUserInput(v); }}
          emptyHint="该范围内没有用户消费"
        />
      </div>

      {/* 构成：类型环形图 + 模型/线路表 */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <Panel title="费用构成" sub="按生成类型" className="xl:col-span-2">
          <DonutChart segments={typeSegments} total={typeTotal} />
        </Panel>

        <Panel
          title="模型消耗"
          sub={`${timeRange.label} · 按模型名汇总 · ${filteredByModel.length} 个规格`}
          className="xl:col-span-3"
          right={(
            <div className="flex flex-wrap items-center justify-end gap-2">
              <AppSelect
                value={modelNameFilter}
                onChange={(event) => setModelNameFilter(event.target.value)}
                className="h-8 max-w-[220px] px-2 rounded-md text-2xs outline-none"
                style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-secondary)" }}
                title="按模型名查看消耗"
                aria-label="按模型名查看消耗"
              >
                <option value="">全部模型</option>
                {modelNameFilter && !modelNameOptions.includes(modelNameFilter) && (
                  <option value={modelNameFilter}>{modelNameFilter}</option>
                )}
                {modelNameOptions.map((modelName) => <option key={modelName} value={modelName}>{modelName}</option>)}
              </AppSelect>
              <AppSelect
                value={providerFilter}
                onChange={(event) => setProviderFilter(event.target.value)}
                className="h-8 max-w-[180px] px-2 rounded-md text-2xs outline-none"
                style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-secondary)" }}
                title="按线路查看模型消耗"
                aria-label="按线路查看模型消耗"
              >
                <option value="">全部线路</option>
                {providerOptions.map((provider) => <option key={provider} value={provider}>{provider}</option>)}
              </AppSelect>
            </div>
          )}
        >
        <div
          className="mb-3 grid grid-cols-2 overflow-hidden rounded-md border md:grid-cols-4"
          style={{ borderColor: "var(--color-border-secondary)", backgroundColor: "var(--color-bg-primary)" }}
        >
          {[
            ["筛选费用", formatMoney(selectedModelSummary.totalCost)],
            ["调用次数", selectedModelSummary.count.toLocaleString("zh-CN")],
            ["输入 / 输出 Token", `${selectedModelSummary.promptTokens.toLocaleString("zh-CN")} / ${selectedModelSummary.completionTokens.toLocaleString("zh-CN")}`],
            ["视频时长", `${selectedModelSummary.videoSeconds.toLocaleString("zh-CN")} 秒`],
          ].map(([label, value], index) => (
            <div
              key={label}
              className="min-w-0 px-3 py-2.5"
              style={index > 0 ? { borderLeft: "1px solid var(--color-border-secondary)" } : undefined}
            >
              <div className="text-label truncate">{label}</div>
              <div className="mt-0.5 truncate font-mono-num text-xs font-semibold" style={{ color: index === 0 ? ACCENT : "var(--color-text-primary)" }}>{value}</div>
            </div>
          ))}
        </div>
        {!filteredByModel.length ? (
          <EmptyBlock text="暂无模型消耗数据" hint={(providerFilter || modelNameFilter) ? "当前模型或线路在该时间范围内没有记录" : undefined} />
        ) : (
          <div className="max-h-[420px] overflow-auto custom-scrollbar">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10" style={{ backgroundColor: "var(--color-bg-secondary)" }}>
                <tr className="text-label" style={{ textAlign: "left" }}>
                  <th className="font-medium pb-2.5 pr-4">类型</th>
                  <th className="font-medium pb-2.5 pr-4">模型名</th>
                  <th className="font-medium pb-2.5 pr-4">线路</th>
                  <th className="font-medium pb-2.5 pr-4 text-right">次数</th>
                  <th className="font-medium pb-2.5 pr-4 text-right">费用</th>
                  <th className="font-medium pb-2.5 w-[22%]">占比</th>
                </tr>
              </thead>
              <tbody>
                {filteredByModel.map((row, idx) => {
                  const share = kpis.totalCost > 0 ? Number(row.totalCost || 0) / kpis.totalCost : 0;
                  const displayModel = row.subModel || row.model || "未标记模型";
                  const baseModel = row.subModel && row.model && row.subModel !== row.model ? row.model : "";
                  return (
                    <tr key={row.key || idx} style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
                      <td className="py-2 pr-4"><TypeBadge type={row.type} /></td>
                      <td className="py-2 pr-4 text-xs font-medium" style={{ color: "var(--color-text-primary)" }} title={displayModel}>{shortText(displayModel, 36)}</td>
                      <td className="py-2 pr-4 text-xs" style={{ color: "var(--color-text-muted)" }} title={[row.provider, baseModel].filter(Boolean).join(" · ")}>
                        {shortText([row.provider, baseModel].filter(Boolean).join(" · ") || "-", 30)}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono-num text-xs">{row.count || 0}</td>
                      <td className="py-2 pr-4 text-right font-mono-num text-xs font-semibold" style={{ color: ACCENT }}>{formatMoney(row.totalCost)}</td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="h-1 flex-1 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                            <div className="h-full rounded-full" style={{ width: `${Math.max(1, share * 100)}%`, backgroundColor: ACCENT, opacity: 0.75 }} />
                          </div>
                          <span className="font-mono-num text-2xs w-11 text-right" style={{ color: "var(--color-text-muted)" }}>
                            {(share * 100).toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </Panel>
      </div>

      {/* 明细 */}
      <Panel
        title="费用明细"
        sub={`${timeRange.label} · 逐条任务记录`}
        right={(
          <div className="flex items-center gap-2">
            <input
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              placeholder="来源筛选"
              className="h-7 px-2 rounded-md text-2xs w-28 outline-none"
              style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-secondary)" }}
              title="按来源筛选（仅影响本表）"
            />
            <AppSelect
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-7 px-2 rounded-md text-2xs outline-none"
              style={{ backgroundColor: "var(--color-bg-tertiary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-secondary)" }}
            >
              {[100, 300, 500, 1000].map((n) => <option key={n} value={n}>最近 {n} 条</option>)}
            </AppSelect>
          </div>
        )}
      >
        {!filteredDetailRows.length ? (
          <EmptyBlock text="该范围内没有费用记录" hint={sourceFilter ? "没有匹配该来源的记录" : "调整时间范围或筛选条件"} />
        ) : (
          <div className="overflow-x-auto -mx-1 px-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-label" style={{ textAlign: "left" }}>
                  {["时间", "用户", "项目", "类型", "来源", "模型", "规格", "费用"].map((h, i) => (
                    <th key={h} className={`font-medium pb-2.5 pr-4 ${i === 7 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDetailRows.map((row, idx) => (
                  <tr key={row.internalTaskId || row.id || idx} style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
                    <td className="py-2 pr-4 font-mono-num text-2xs whitespace-nowrap" style={{ color: "var(--color-text-muted)" }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-"}
                    </td>
                    <td className="py-2 pr-4 text-xs">{displayUserId(row.userId)}</td>
                    <td className="py-2 pr-4 text-xs" style={{ color: "var(--color-text-secondary)" }}>{shortText(projectLabel(row.projectId), 18)}</td>
                    <td className="py-2 pr-4"><TypeBadge type={row.type} /></td>
                    <td className="py-2 pr-4 text-xs" style={{ color: "var(--color-text-muted)" }}>{shortText(row.source, 24)}</td>
                    <td className="py-2 pr-4 text-xs" style={{ color: "var(--color-text-muted)" }}>{shortText(`${row.model || "-"}${row.subModel && row.subModel !== row.model ? ` / ${row.subModel}` : ""}`, 32)}</td>
                    <td className="py-2 pr-4 text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>{formatCostSpec(row)}</td>
                    <td className="py-2 text-right font-mono-num text-xs font-semibold whitespace-nowrap" style={{ color: ACCENT }}>{formatDetailedMoney(row.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* 数据维护（低频操作，默认收起） */}
      <details
        className="rounded-lg border group"
        style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-secondary)" }}
      >
        <summary className="px-4 py-3 cursor-pointer select-none flex items-center justify-between">
          <span className="text-[13px] font-semibold" style={{ color: "var(--color-text-primary)" }}>数据维护</span>
          <span className="text-label">回填与历史费用重算 · 点击展开</span>
        </summary>
        <div className="px-4 pb-4 pt-1 border-t animate-in fade-in duration-150" style={{ borderColor: "var(--color-border-secondary)" }}>
          <div className="flex flex-wrap items-center gap-3 pt-3">
            <button
              type="button"
              onClick={() => runBackfill(true)}
              className="h-8 px-3.5 rounded-md text-xs font-medium border cursor-pointer transition-colors duration-150"
              style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}
            >
              试算空费用
            </button>
            <button
              type="button"
              onClick={() => runBackfill(false)}
              className="h-8 px-3.5 rounded-md text-xs font-semibold cursor-pointer transition-colors duration-150"
              style={{ backgroundColor: ACCENT, color: "var(--color-bg-primary)" }}
            >
              回填空费用
            </button>
            <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
              对历史 cost 为空的记录按当前价表补价；写入前会先弹确认。
            </span>
          </div>
          {maintenanceResult && (
            <div className="mt-3 rounded-lg border p-3 grid grid-cols-2 md:grid-cols-6 gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ borderColor: "var(--color-border-secondary)" }}>
              {[
                ["模式", maintenanceResult.dryRun ? "试算" : "写入"],
                ["处理", maintenanceResult.processed ?? 0],
                ["写入", maintenanceResult.updated ?? 0],
                ["零费用", maintenanceResult.zeroCost ?? 0],
                ["本批费用", formatMoney(maintenanceResult.calculatedCost)],
                ["剩余空值", maintenanceResult.remainingAfter ?? 0],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-label">{label}</div>
                  <div className="font-mono-num text-sm font-semibold mt-0.5" style={{ color: "var(--color-text-primary)" }}>{value}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-3" style={{ borderColor: "var(--color-border-secondary)" }}>
            <button
              type="button"
              onClick={runEngGuaVideoRecompute}
              disabled={engGuaVideoRecomputing}
              className="h-8 px-3.5 rounded-md text-xs font-semibold cursor-pointer transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: "var(--color-purple-primary)", color: "white" }}
            >
              {engGuaVideoRecomputing ? "正在重算..." : "重算 EngGuaVideo 历史费用"}
            </button>
            <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
              仅处理权威账本中 EngGuaVideo 已成功且费用为 0/空的任务，按标准/fast、480p/720p 与实际时长补价。
            </span>
          </div>
          {engGuaVideoRecomputeResult && (
            <div
              className="mt-3 rounded-lg border px-3 py-2.5 text-xs animate-in fade-in slide-in-from-bottom-2 duration-300"
              style={{ borderColor: "var(--color-border-secondary)", color: "var(--color-text-secondary)" }}
            >
              EngGuaVideo 历史费用重算完成，共更新
              <strong className="mx-1 font-mono-num" style={{ color: "var(--color-text-primary)" }}>
                {engGuaVideoRecomputeResult.changed ?? 0}
              </strong>
              条记录。
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

export default CostAnalyticsTab;
